import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { Tooltip, Box, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { colors } from './Theme/WebTheme';

const useStyles = makeStyles((theme) => ({
  clickableLink: {
    cursor: 'pointer',
    color: colors.HEADER,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 4px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(33, 150, 243, 0.08)',
      color: colors.BLUE || '#1976d2',
      textDecoration: 'underline',
    },
    '&:active': {
      backgroundColor: 'rgba(33, 150, 243, 0.12)',
    }
  },
  icon: {
    fontSize: '16px',
    opacity: 0.7,
  },
  loadingContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  disabledLink: {
    cursor: 'not-allowed',
    opacity: 0.6,
    color: 'inherit',
  }
}));

/**
 * ClickableUserLink Component
 * 
 * Makes user names and phone numbers clickable to navigate to user details.
 * Automatically detects user type (driver/customer) and navigates to the appropriate page.
 * 
 * @param {Object} props
 * @param {string} props.value - The value to display (name or phone number)
 * @param {string} props.type - Type of link: 'driver', 'customer', or 'auto' (auto-detect)
 * @param {string} props.searchField - Field to search by: 'name', 'phone', 'email'
 * @param {string} props.userId - Direct user ID if available (bypasses search)
 * @param {boolean} props.showIcon - Whether to show an icon next to the link
 * @param {string} props.iconType - Type of icon: 'person', 'phone', 'car'
 * @param {boolean} props.disabled - Whether the link is disabled
 * @param {string} props.tooltipText - Custom tooltip text
 * @param {Object} props.style - Custom styles
 */
const ClickableUserLink = ({
  value,
  type = 'auto',
  searchField = 'name',
  userId = null,
  showIcon = true,
  iconType = 'person',
  disabled = false,
  tooltipText = null,
  style = {},
  ...otherProps
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Narrow selector — was `state.usersdata` (the entire slice), which made
  // every ClickableUserLink re-evaluate on any change to drivers, staticusers,
  // bookings-derived fields, etc. With ~150 link instances on a Complain
  // page × ~20k users in the slice, this triggered millions of comparisons
  // per state change and was the dominant page-load cost.
  const usersList = useSelector(state => state.usersdata?.users);
  const userRole = useSelector(state => state.auth?.profile?.usertype);
  const [loading, setLoading] = useState(false);

  // Check if user has permission to view user details
  const hasPermission = userRole && [
    'admin',
    'fleetadmin',
    'finance',
    'callcenteragent',
    'callcentersupervisor'
  ].includes(userRole);

  // Fast path: when the caller already passed userId + an explicit type,
  // there is no work to do up front — we already know where to navigate
  // and the row already shows the name. Previously the component still
  // scanned the entire users array on mount JUST to populate a tooltip.
  // This single change eliminates the per-row O(n) scan that made small
  // pages (SOS, Complain) take minutes to load. The lookup only runs as
  // a fallback when the caller couldn't supply userId.
  const needsLookup = !userId && !!usersList && !!value;

  const userInfo = useMemo(() => {
    if (!needsLookup) return null;
    let foundUser = null;
    if (searchField === 'phone') {
      const cleanValue = value.replace(/[\s\-()]/g, '');
      foundUser = usersList.find(user => {
        const userPhone = (user.mobile || user.phone || '').replace(/[\s\-()]/g, '');
        return userPhone === cleanValue || userPhone.endsWith(cleanValue) || cleanValue.endsWith(userPhone);
      });
    } else if (searchField === 'name') {
      foundUser = usersList.find(user => {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        return fullName.toLowerCase() === value.toLowerCase();
      });
    } else if (searchField === 'email') {
      foundUser = usersList.find(user =>
        user.email?.toLowerCase() === value.toLowerCase()
      );
    }
    if (!foundUser) return null;
    return {
      id: foundUser.id,
      type: foundUser.usertype,
      name: `${foundUser.firstName || ''} ${foundUser.lastName || ''}`.trim(),
      phone: foundUser.mobile || foundUser.phone,
      email: foundUser.email
    };
  }, [needsLookup, searchField, value, usersList]);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled || !hasPermission || loading) return;

    setLoading(true);

    try {
      let targetUserId = userId;
      let userType = type;

      // If we have userInfo from search, use it
      if (userInfo) {
        targetUserId = userInfo.id;
        userType = userInfo.type;
      }

      // Auto-detect user type if needed. Only runs on click, never on
      // mount — keeps the per-row scan out of the hot render path.
      if (userType === 'auto' && targetUserId) {
        let u = usersList && usersList.find(x => x.id === targetUserId);
        if (!u) {
          // Not in the loaded array (e.g. a customer in a call-center session
          // that no longer pre-loads customers) — resolve this single record
          // on demand instead of guessing.
          try { u = await dispatch(api.resolveSingleUser({ uid: targetUserId })); } catch (_) { /* ignore */ }
        }
        if (u) userType = u.usertype;
      }

      // No userId on the row and nothing found locally — resolve by phone/name
      // via an indexed server query before falling back to the search page.
      if (!targetUserId) {
        try {
          if (searchField === 'phone') {
            const r = await dispatch(api.lookupUserByPhone(value));
            if (r && r.id) { targetUserId = r.id; userType = r.usertype; }
          } else if (searchField === 'name') {
            const list = await dispatch(api.lookupUsersByNamePrefix(value, 1));
            if (list && list[0]) { targetUserId = list[0].id; userType = list[0].usertype; }
          }
        } catch (_) { /* fall through to search page */ }
      }

      // Navigate based on resolved user type.
      if (targetUserId && userType === 'driver') {
        navigate(`/users/driverdetails/${targetUserId}`);
      } else if (targetUserId && (userType === 'customer' || userType === 'rider')) {
        navigate(`/users/customerdetails/${targetUserId}`);
      } else if (targetUserId && userType && userType !== 'auto') {
        // Other user types — the appropriate users tab.
        const tabIndex = userType === 'fleetadmin' ? 2 : userType === 'admin' ? 4 : 0;
        navigate(`/users/${tabIndex}`);
      } else {
        // Unresolved — users page with a search hint.
        navigate('/users/0', { state: { searchQuery: value } });
      }
    } catch (error) {
      console.error('Error navigating to user details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Don't render as link if no permission or no value
  if (!value || !hasPermission) {
    return <span style={style} {...otherProps}>{value || '-'}</span>;
  }

  // Determine icon to show
  const getIcon = () => {
    if (!showIcon) return null;
    
    switch (iconType) {
      case 'phone':
        return <PhoneIcon className={classes.icon} />;
      case 'car':
        return <DirectionsCarIcon className={classes.icon} />;
      case 'person':
      default:
        return <PersonIcon className={classes.icon} />;
    }
  };

  // Determine tooltip text
  const getTooltipText = () => {
    if (tooltipText) return tooltipText;
    
    if (userInfo) {
      const userTypeText = userInfo.type === 'driver' ? 'Driver' : 
                           userInfo.type === 'customer' ? 'Customer' : 
                           userInfo.type;
      return `View ${userTypeText} Details: ${userInfo.name}`;
    }
    
    return `Click to view user details`;
  };

  const linkContent = (
    <Box
      className={`${classes.clickableLink} ${disabled ? classes.disabledLink : ''}`}
      onClick={handleClick}
      style={style}
      {...otherProps}
    >
      {getIcon()}
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={14} />
          <span>{value}</span>
        </Box>
      ) : (
        <span>{value}</span>
      )}
    </Box>
  );

  return (
    <Tooltip title={getTooltipText()} arrow placement="top">
      {linkContent}
    </Tooltip>
  );
};

export default ClickableUserLink;
